import { createTheme, alpha } from '@mui/material/styles';

/* ─────────────────────────────────────────
   HOTEL DESK — PREMIUM ENTERPRISE THEME
   Brand: Primary Red #C8102E on Dark Navy.
   Red is used ONLY as an accent (primary buttons, selected menu,
   focus, badges, highlights); the surface stays clean navy/slate/white.
   This single theme feeds one root ThemeProvider, so every MUI
   component across the app inherits the brand — no logic changes.
────────────────────────────────────────── */
const P = {
  // Brand (accent only)
  brand:      '#C8102E',
  brandLight: '#E23A4E',
  brandDark:  '#9E0C22',
  // Semantic
  success:     '#16A34A',
  successLight:'#22C55E',
  successDark: '#15803D',
  danger:      '#DC2626',
  dangerLight: '#EF4444',
  dangerDark:  '#B91C1C',
  warning:     '#F59E0B',
  warningLight:'#FBBF24',
  warningDark: '#D97706',
  info:        '#2563EB',
  infoLight:   '#3B82F6',
  infoDark:    '#1D4ED8',
  // Neutrals (navy → slate → paper)
  slate900:  '#0F172A',
  slate800:  '#1E293B',
  slate700:  '#334155',
  slate600:  '#475569',
  slate500:  '#64748B',
  slate400:  '#94A3B8',
  slate300:  '#CBD5E1',
  slate200:  '#E5E7EB',
  slate100:  '#F1F5F9',
  slate50:   '#F8FAFC',
};

/* Refined shadow scale — subtle, layered, non-colored */
const shadows = [
  'none',
  `0 1px 2px ${alpha(P.slate900, 0.05)}`,
  `0 1px 3px ${alpha(P.slate900, 0.06)}, 0 1px 2px ${alpha(P.slate900, 0.04)}`,
  `0 2px 4px ${alpha(P.slate900, 0.06)}, 0 1px 2px ${alpha(P.slate900, 0.04)}`,
  `0 4px 8px ${alpha(P.slate900, 0.06)}, 0 2px 4px ${alpha(P.slate900, 0.04)}`,
  `0 4px 10px ${alpha(P.slate900, 0.07)}, 0 2px 4px ${alpha(P.slate900, 0.04)}`,
  `0 6px 14px ${alpha(P.slate900, 0.07)}, 0 3px 6px ${alpha(P.slate900, 0.04)}`,
  `0 8px 16px ${alpha(P.slate900, 0.08)}, 0 4px 8px ${alpha(P.slate900, 0.04)}`,
  `0 10px 20px ${alpha(P.slate900, 0.08)}, 0 4px 10px ${alpha(P.slate900, 0.04)}`,
  `0 12px 24px ${alpha(P.slate900, 0.09)}, 0 6px 12px ${alpha(P.slate900, 0.04)}`,
  `0 14px 28px ${alpha(P.slate900, 0.09)}, 0 6px 14px ${alpha(P.slate900, 0.04)}`,
  `0 16px 32px ${alpha(P.slate900, 0.10)}, 0 8px 16px ${alpha(P.slate900, 0.04)}`,
  `0 18px 36px ${alpha(P.slate900, 0.10)}, 0 8px 18px ${alpha(P.slate900, 0.05)}`,
  `0 20px 40px ${alpha(P.slate900, 0.11)}, 0 10px 20px ${alpha(P.slate900, 0.05)}`,
  `0 22px 44px ${alpha(P.slate900, 0.11)}, 0 10px 22px ${alpha(P.slate900, 0.05)}`,
  `0 24px 48px ${alpha(P.slate900, 0.12)}, 0 12px 24px ${alpha(P.slate900, 0.05)}`,
  `0 28px 56px ${alpha(P.slate900, 0.12)}, 0 14px 28px ${alpha(P.slate900, 0.05)}`,
  `0 32px 64px ${alpha(P.slate900, 0.13)}, 0 16px 32px ${alpha(P.slate900, 0.05)}`,
  `0 36px 72px ${alpha(P.slate900, 0.13)}, 0 18px 36px ${alpha(P.slate900, 0.06)}`,
  `0 40px 80px ${alpha(P.slate900, 0.14)}, 0 20px 40px ${alpha(P.slate900, 0.06)}`,
  `0 44px 88px ${alpha(P.slate900, 0.14)}, 0 22px 44px ${alpha(P.slate900, 0.06)}`,
  `0 48px 96px ${alpha(P.slate900, 0.15)}, 0 24px 48px ${alpha(P.slate900, 0.06)}`,
  `0 52px 100px ${alpha(P.slate900, 0.15)}, 0 26px 52px ${alpha(P.slate900, 0.07)}`,
  `0 56px 112px ${alpha(P.slate900, 0.16)}, 0 28px 56px ${alpha(P.slate900, 0.07)}`,
  `0 60px 120px ${alpha(P.slate900, 0.18)}, 0 30px 60px ${alpha(P.slate900, 0.08)}`,
];

const theme = createTheme({
  /* ── Palette ── */
  palette: {
    mode: 'light',
    primary: {
      main:         P.brand,
      light:        P.brandLight,
      dark:         P.brandDark,
      contrastText: '#ffffff',
    },
    secondary: {
      main:         P.success,
      light:        P.successLight,
      dark:         P.successDark,
      contrastText: '#ffffff',
    },
    error:   { main: P.danger,  light: P.dangerLight,  dark: P.dangerDark },
    warning: { main: P.warning, light: P.warningLight, dark: P.warningDark },
    info:    { main: P.info,    light: P.infoLight,    dark: P.infoDark },
    success: { main: P.success, light: P.successLight, dark: P.successDark },
    grey: {
      50:  P.slate50,  100: P.slate100, 200: P.slate200,
      300: P.slate300, 400: P.slate400, 500: P.slate500,
      600: P.slate600, 700: P.slate700, 800: P.slate800, 900: P.slate900,
    },
    background: { default: '#F8FAFC', paper: '#ffffff' },
    text: { primary: P.slate900, secondary: P.slate600, disabled: P.slate400 },
    divider: '#E5E7EB',
  },

  /* ── Typography — Inter ── */
  typography: {
    fontFamily: '"Inter", "Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontWeightLight:   300,
    fontWeightRegular: 400,
    fontWeightMedium:  500,
    fontWeightBold:    700,
    h1: { fontSize: '2.25rem',  fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1.2,  color: P.slate900 },
    h2: { fontSize: '1.75rem',  fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.25, color: P.slate900 },
    h3: { fontSize: '1.375rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.3,  color: P.slate900 },
    h4: { fontSize: '1.125rem', fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.35, color: P.slate800 },
    h5: { fontSize: '1rem',     fontWeight: 600, letterSpacing: '-0.005em',lineHeight: 1.4,  color: P.slate800 },
    h6: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5,          color: P.slate800 },
    subtitle1: { fontSize: '0.875rem',   fontWeight: 500, lineHeight: 1.5 },
    subtitle2: { fontSize: '0.8125rem',  fontWeight: 500, lineHeight: 1.5 },
    body1:     { fontSize: '0.875rem',   fontWeight: 400, lineHeight: 1.6 },
    body2:     { fontSize: '0.8125rem',  fontWeight: 400, lineHeight: 1.6 },
    caption:   { fontSize: '0.72rem',    fontWeight: 500, letterSpacing: '0.3px' },
    overline:  { fontSize: '0.65rem',    fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' },
    button:    { fontSize: '0.8125rem',  fontWeight: 600, letterSpacing: '0.2px', textTransform: 'none' },
  },

  shape: { borderRadius: 10 },
  shadows,

  /* ── Component overrides ── */
  components: {

    /* Paper */
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: `1px solid ${alpha(P.slate200, 0.85)}`,
        },
        elevation1: { boxShadow: `0 1px 3px ${alpha(P.slate900,0.05)}, 0 4px 16px ${alpha(P.slate900,0.04)}` },
        elevation2: { boxShadow: `0 2px 6px ${alpha(P.slate900,0.07)}, 0 8px 24px ${alpha(P.slate900,0.05)}` },
        elevation3: { boxShadow: `0 4px 12px ${alpha(P.slate900,0.08)}, 0 12px 32px ${alpha(P.slate900,0.06)}` },
      },
    },

    /* Card — 16px radius, soft shadow */
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: '16px',
          border: `1px solid ${alpha(P.slate200, 0.9)}`,
          boxShadow: `0 1px 3px ${alpha(P.slate900,0.04)}, 0 4px 16px ${alpha(P.slate900,0.04)}`,
          backgroundImage: 'none',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: { padding: '18px 22px', '&:last-child': { paddingBottom: '18px' } },
      },
    },

    /* Button — solid brand, no gradients (minimal / premium) */
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: '10px',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.8125rem',
          letterSpacing: '0.15px',
          padding: '8px 18px',
          transition: 'all 0.18s ease',
          '&:active': { transform: 'scale(0.98)' },
        },
        sizeSmall: { padding: '5px 12px', fontSize: '0.75rem',  borderRadius: '8px' },
        sizeLarge: { padding: '12px 28px',fontSize: '0.9375rem', borderRadius: '12px' },
        // Only color="primary" (the red brand) is re-skinned to blue.
        // Semantic colors keep their meaning: secondary/success=green, error=red,
        // warning=orange, info=blue. Outlined buttons stay outlined.
        contained: {
          '&:hover': { transform: 'translateY(-1px)' },
        },
        containedPrimary: {
          color: '#fff',
          backgroundColor: '#2563eb',
          backgroundImage: 'linear-gradient(135deg,#2563eb,#1d4ed8)',
          boxShadow: `0 2px 8px ${alpha('#2563eb',0.30)}`,
          '&:hover': {
            backgroundImage: 'linear-gradient(135deg,#1d4ed8,#1e40af)',
            boxShadow: `0 6px 18px ${alpha('#2563eb',0.40)}`,
          },
          '&.Mui-disabled': { backgroundImage: 'none', boxShadow: 'none' },
        },
        containedSecondary: {
          backgroundColor: P.success,
          boxShadow: `0 1px 2px ${alpha(P.success,0.25)}`,
          '&:hover': {
            backgroundColor: P.successDark,
            boxShadow: `0 4px 14px ${alpha(P.success,0.35)}`,
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': { borderWidth: '1.5px' },
        },
        outlinedPrimary: {
          border: 'none',
          color: '#fff',
          backgroundColor: '#2563eb',
          backgroundImage: 'linear-gradient(135deg,#2563eb,#1d4ed8)',
          boxShadow: `0 2px 8px ${alpha('#2563eb',0.30)}`,
          '&:hover': {
            border: 'none',
            backgroundImage: 'linear-gradient(135deg,#1d4ed8,#1e40af)',
            boxShadow: `0 6px 18px ${alpha('#2563eb',0.40)}`,
          },
          '&.Mui-disabled': { backgroundImage: 'none', boxShadow: 'none', color: 'rgba(0,0,0,0.26)' },
        },
        text: { color: '#2563eb', '&:hover': { backgroundColor: alpha('#2563eb', 0.06) } },
      },
    },

    /* IconButton */
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          color: '#2563eb',
          transition: 'all 0.15s ease',
          '& .MuiSvgIcon-root': { color: '#2563eb' },
          '&.Mui-disabled .MuiSvgIcon-root': { color: 'rgba(0,0,0,0.26)' },
          '&:hover': { backgroundColor: alpha('#2563eb', 0.08) },
        },
      },
    },

    /* TextField */
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'small' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          fontSize: '0.875rem',
          backgroundColor: '#ffffff',
          transition: 'box-shadow 0.18s ease',
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: P.brand },
          '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(P.brand, 0.12)}` },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: P.brand,
            borderWidth: '1.5px',
          },
          '&.Mui-error.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(P.danger, 0.12)}` },
        },
        notchedOutline: {
          borderColor: alpha(P.slate200, 0.9),
          transition: 'border-color 0.18s ease',
        },
        input: { fontSize: '0.875rem', '&::placeholder': { color: P.slate400, opacity: 1 } },
        sizeSmall: { fontSize: '0.8125rem' },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          color: P.slate500,
          fontWeight: 500,
          '&.Mui-focused': { color: P.brand },
          '&.Mui-error': { color: P.danger },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          fontWeight: 600,
          color: P.slate600,
          '&.Mui-focused': { color: P.brand },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { fontSize: '0.72rem', fontWeight: 500, marginTop: '4px' },
      },
    },

    /* Select */
    MuiSelect: {
      styleOverrides: {
        icon: { color: P.slate500 },
        select: {
          whiteSpace: 'normal !important',
          wordBreak: 'break-word',
          lineHeight: 1.3,
          minHeight: 'unset !important',
          textOverflow: 'clip !important',
          overflow: 'visible !important',
          paddingTop: '6px !important',
          paddingBottom: '6px !important',
        },
      },
    },

    /* Autocomplete */
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: '12px',
          border: `1px solid ${alpha(P.slate200, 0.95)}`,
          boxShadow: `0 8px 32px ${alpha(P.slate900, 0.12)}`,
          marginTop: '4px',
        },
        option: {
          borderRadius: '8px',
          margin: '2px 6px',
          padding: '8px 10px',
          fontSize: '0.8125rem',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          lineHeight: 1.35,
          '&[aria-selected="true"]': {
            backgroundColor: alpha(P.brand, 0.08),
            color: P.brand,
            fontWeight: 600,
          },
          '&:hover': { backgroundColor: P.slate50 },
        },
        listbox: { padding: '6px', maxHeight: 320 },
        input: {
          textOverflow: 'ellipsis',
        },
      },
    },

    /* Menu */
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: '12px',
          border: `1px solid ${alpha(P.slate200, 0.95)}`,
          boxShadow: `0 8px 32px ${alpha(P.slate900, 0.12)}`,
          marginTop: '4px',
          maxHeight: 320,
        },
        list: { padding: '6px' },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          margin: '1px 4px',
          padding: '8px 12px',
          fontSize: '0.8125rem',
          fontWeight: 500,
          transition: 'background 0.12s',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          lineHeight: 1.35,
          minHeight: 'unset',
          '&:hover': { backgroundColor: P.slate50 },
          '&.Mui-selected': {
            backgroundColor: alpha(P.brand, 0.08),
            color: P.brand,
            fontWeight: 600,
            '&:hover': { backgroundColor: alpha(P.brand, 0.12) },
          },
        },
      },
    },

    /* Table */
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          border: `1px solid ${alpha(P.slate200, 0.9)}`,
          boxShadow: `0 1px 3px ${alpha(P.slate900,0.04)}, 0 4px 16px ${alpha(P.slate900,0.04)}`,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: P.slate50,
            color: P.slate500,
            fontWeight: 700,
            fontSize: '0.68rem',
            letterSpacing: '0.6px',
            textTransform: 'uppercase',
            borderBottom: `2px solid ${P.slate200}`,
            padding: '10px 16px',
            whiteSpace: 'nowrap',
            position: 'sticky',
            top: 0,
            zIndex: 2,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background 0.1s',
          '&:hover': { backgroundColor: P.slate50 },
          '&:last-child td, &:last-child th': { border: 0 },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          color: P.slate700,
          borderColor: alpha(P.slate200, 0.7),
          padding: '10px 16px',
        },
      },
    },

    /* Chip */
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontWeight: 600,
          fontSize: '0.7rem',
          height: '24px',
          letterSpacing: '0.2px',
        },
        filled: {
          '&.MuiChip-colorPrimary':   { backgroundColor: alpha(P.brand,   0.1), color: P.brand },
          '&.MuiChip-colorSecondary': { backgroundColor: alpha(P.success, 0.1), color: P.successDark },
          '&.MuiChip-colorSuccess':   { backgroundColor: alpha(P.success, 0.1), color: P.successDark },
          '&.MuiChip-colorError':     { backgroundColor: alpha(P.danger,  0.1), color: P.dangerDark },
          '&.MuiChip-colorWarning':   { backgroundColor: alpha(P.warning, 0.1), color: P.warningDark },
          '&.MuiChip-colorInfo':      { backgroundColor: alpha(P.info,    0.1), color: P.infoDark },
        },
        outlined: {
          '&.MuiChip-colorPrimary': { borderColor: alpha(P.brand, 0.35), color: P.brand },
        },
        label: { paddingLeft: '8px', paddingRight: '8px' },
        deleteIcon: { fontSize: '14px' },
      },
    },

    /* Tooltip */
    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: {
        tooltip: {
          backgroundColor: P.slate900,
          fontSize: '0.72rem',
          fontWeight: 500,
          borderRadius: '8px',
          padding: '6px 10px',
          boxShadow: `0 4px 12px ${alpha(P.slate900, 0.25)}`,
          letterSpacing: '0.1px',
        },
        arrow: { color: P.slate900 },
      },
    },

    /* Dialog */
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '16px',
          boxShadow: `0 24px 64px ${alpha(P.slate900, 0.2)}`,
          border: `1px solid ${alpha(P.slate200, 0.5)}`,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontSize: '1rem', fontWeight: 700, color: P.slate900, padding: '20px 24px 12px' },
      },
    },
    MuiDialogContent: {
      styleOverrides: { root: { padding: '12px 24px' } },
    },
    MuiDialogActions: {
      styleOverrides: { root: { padding: '12px 24px 20px', gap: '8px' } },
    },

    /* Tabs */
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: '40px' },
        indicator: { height: '2.5px', borderRadius: '2px 2px 0 0', backgroundColor: P.brand },
        scrollButtons: { '&.Mui-disabled': { opacity: 0.3 } },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.8125rem',
          minHeight: '40px',
          padding: '8px 16px',
          color: P.slate500,
          transition: 'color 0.15s',
          '&.Mui-selected': { color: P.brand, fontWeight: 600 },
          '&:hover': { color: P.brand, opacity: 0.85 },
        },
      },
    },

    /* Alert — solid semantic tints, no gradients */
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          fontSize: '0.8125rem',
          fontWeight: 500,
          border: '1px solid',
        },
        standardSuccess: { backgroundColor: alpha(P.success, 0.06), borderColor: alpha(P.success, 0.25), color: P.successDark },
        standardError:   { backgroundColor: alpha(P.danger,  0.06), borderColor: alpha(P.danger,  0.25), color: P.dangerDark },
        standardWarning: { backgroundColor: alpha(P.warning, 0.06), borderColor: alpha(P.warning, 0.25), color: P.warningDark },
        standardInfo:    { backgroundColor: alpha(P.info,    0.06), borderColor: alpha(P.info,    0.25), color: P.infoDark },
        filledSuccess: { backgroundColor: P.success },
        filledError:   { backgroundColor: P.danger },
        filledWarning: { backgroundColor: P.warning, color: P.slate900 },
        filledInfo:    { backgroundColor: P.info },
        message: { padding: '6px 0' },
      },
    },

    /* LinearProgress */
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: '4px', backgroundColor: alpha(P.brand, 0.1) },
        bar:  { borderRadius: '4px' },
        barColorPrimary:   { backgroundColor: P.brand },
        barColorSecondary: { backgroundColor: P.success },
      },
    },

    /* Switch */
    MuiSwitch: {
      styleOverrides: {
        root: { width: 42, height: 24, padding: 0 },
        switchBase: {
          padding: 3,
          '&.Mui-checked': {
            transform: 'translateX(18px)',
            '& + .MuiSwitch-track': { backgroundColor: P.brand, opacity: 1 },
          },
        },
        thumb: { width: 18, height: 18, boxShadow: `0 1px 4px ${alpha(P.slate900, 0.2)}` },
        track: { borderRadius: 12, backgroundColor: P.slate300, opacity: 1, transition: 'background 0.2s' },
      },
    },

    /* Divider */
    MuiDivider: {
      styleOverrides: { root: { borderColor: '#E5E7EB' } },
    },

    /* Avatar */
    MuiAvatar: {
      styleOverrides: {
        root: { fontWeight: 700, fontSize: '0.875rem' },
        colorDefault: { backgroundColor: alpha(P.brand, 0.15), color: P.brand },
      },
    },

    /* Badge */
    MuiBadge: {
      styleOverrides: {
        badge: { fontSize: '0.6rem', fontWeight: 700, minWidth: '18px', height: '18px', borderRadius: '9px' },
        colorPrimary: { backgroundColor: P.brand },
      },
    },

    /* AppBar */
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: P.slate900,
          borderBottom: `1px solid ${alpha(P.slate200, 0.8)}`,
          boxShadow: `0 1px 8px ${alpha(P.slate900, 0.06)}`,
        },
      },
    },

    /* Toolbar */
    MuiToolbar: {
      styleOverrides: { root: { minHeight: '56px !important' } },
    },

    /* Breadcrumbs */
    MuiBreadcrumbs: {
      styleOverrides: {
        root: { fontSize: '0.8125rem', color: P.slate500 },
        separator: { color: P.slate300 },
      },
    },

    /* Accordion */
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: '12px !important',
          border: `1px solid ${alpha(P.slate200, 0.9)}`,
          boxShadow: 'none',
          backgroundImage: 'none',
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: '4px 0' },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: { fontWeight: 600, fontSize: '0.875rem', minHeight: '48px', '&.Mui-expanded': { minHeight: '48px' } },
        content: { '&.Mui-expanded': { margin: '12px 0' } },
      },
    },

    /* Pagination */
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontWeight: 500,
          border: `1px solid ${P.slate200}`,
          '&.Mui-selected': {
            backgroundColor: P.brand,
            color: '#fff',
            borderColor: P.brand,
            '&:hover': { backgroundColor: P.brandDark },
          },
          '&:hover': { backgroundColor: P.slate50 },
        },
      },
    },

    /* Skeleton */
    MuiSkeleton: {
      styleOverrides: {
        root: { borderRadius: '8px', backgroundColor: alpha(P.slate200, 0.7) },
        wave: { '&::after': { background: `linear-gradient(90deg, transparent, ${alpha('#fff', 0.6)}, transparent)` } },
      },
    },

    /* Snackbar */
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          fontSize: '0.8125rem',
          fontWeight: 500,
          boxShadow: `0 8px 24px ${alpha(P.slate900, 0.2)}`,
          backgroundColor: P.slate800,
        },
      },
    },

    /* List */
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          transition: 'all 0.15s ease',
          '&.Mui-selected': {
            backgroundColor: alpha(P.brand, 0.1),
            color: P.brand,
            fontWeight: 600,
            '&:hover': { backgroundColor: alpha(P.brand, 0.14) },
          },
          '&:hover': { backgroundColor: alpha(P.slate900, 0.04) },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: { minWidth: '36px' },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: { fontSize: '0.875rem', fontWeight: 500 },
        secondary: { fontSize: '0.75rem' },
      },
    },

    /* Fab — solid brand */
    MuiFab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.8125rem',
          boxShadow: `0 4px 14px ${alpha(P.slate900, 0.15)}`,
          '&:hover': { boxShadow: `0 6px 20px ${alpha(P.slate900, 0.2)}` },
        },
        primary: {
          backgroundColor: P.brand,
          '&:hover': { backgroundColor: P.brandDark },
        },
      },
    },

    /* Step */
    MuiStepLabel: {
      styleOverrides: {
        label: { fontSize: '0.8125rem', fontWeight: 500 },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: { '&.Mui-active': { color: P.brand }, '&.Mui-completed': { color: P.success } },
      },
    },

    /* Drawer */
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundImage: 'none' },
      },
    },

    /* Date/Time Picker */
    MuiPickersDay: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontWeight: 500,
          '&.Mui-selected': {
            backgroundColor: P.brand,
            '&:hover': { backgroundColor: P.brandDark },
          },
        },
      },
    },
  },
});

export default theme;
